'use client';

import React, {
  type CSSProperties,
  type PropsWithChildren,
  useLayoutEffect,
  useRef,
} from 'react';
import { useMinLoadingState } from 'components/DropZone/useMinLoadingState';
import { useReducedMotion } from 'framer-motion';
import { theme } from '@frameio/vapor-theme';
import styles from './Toast.module.css';
import type { ToastLoadingProps } from './ToastTypes';

export type ToastDisplayProps = Required<
  PropsWithChildren<
    ToastLoadingProps & {
      /**
       * Reference to the toast element -- needed to animate the toast
       */
      toastRef: React.RefObject<HTMLDivElement | null>;
    }
  >
>;

/**
 * Animates the toast when the loading state changes
 */
export function ToastAnimationDisplay({
  children,
  isLoading: isLoadingProp,
  toastRef,
}: ToastDisplayProps) {
  const isLoading = useMinLoadingState(isLoadingProp, 750);
  const existingSize = useRef<{ width: number; height: number } | null>(null);
  const reducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    const node = toastRef.current;
    if (!node) {
      return;
    }
    const parent = node.parentElement;
    if (!parent) {
      return;
    }
    const shouldAnimate = existingSize.current !== null;

    // determine the size of the child node given a non-defined
    // width of the parent
    parent.style.width = 'auto';
    const rect = node.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    // set the width and height of the child with absolute
    // units to prevent resizing during animations
    const existingW = node.style.width;
    const existingH = node.style.height;

    node.style.width = `${w}px`;
    node.style.height = `${h}px`;

    // After the animation has run, restore the parent's width and height
    // to 'auto' and the child node to its original size. This
    // enables the toast to grow and shrink as needed when
    // the viewport changes size.
    function setParentStyle(w: number, h: number) {
      if (!node || !parent || !existingSize) {
        return;
      }
      if (isLoading) {
        // the loading toast renders using absolute positioning
        // to overlay the existing toast. We need to ensure the parent
        // has a defined width and height so that the
        // toast parent renders the proper size.
        parent.style.width = `${w}px`;
        parent.style.height = `${h}px`;
      } else {
        parent.style.width = 'auto';
        parent.style.height = 'auto';
      }

      node.style.width = existingW;
      node.style.height = existingH;
    }

    if (shouldAnimate) {
      parent.animate(
        [
          {
            width: `${existingSize.current?.width}px`,
            height: `${existingSize.current?.height}px`,
          },
          { width: `${w}px`, height: `${h}px` },
        ],
        {
          duration: reducedMotion ? 0 : 450,
          easing: 'ease',
        }
      ).onfinish = () => {
        setParentStyle(w, h);
      };
    } else {
      setParentStyle(w, h);
    }

    // record the size of the child node so that we can
    // animate from that size if our loading state changes
    if (w > 0 && h > 0) {
      existingSize.current = { width: w, height: h };
    }
  }, [isLoading, reducedMotion, toastRef]);

  const cssVars = {
    '--vapor-toast-horizontal-padding': theme.spaces[12],
    '--vapor-toast-min-height': '56px',
    '--vapor-toast-vertical-padding': theme.spaces[12],
    '--vapor-toast-radius': theme.radii[8],
  } as CSSProperties;

  return (
    <div className={styles.styledToast} role="status" style={cssVars}>
      {children}
    </div>
  );
}
